import * as React from 'react';
import {ForwardedRef} from 'react';
import MaterialTable from '../components/material-table';
import {Column, EditCellColumnDef, Filter, MaterialTableProps, Query, QueryResult} from '../components/material-table/types';
import {ListIcons, ListWrappingDiv} from './AdditionalListComponents';
import {compose, Dispatch} from 'redux';
import {connect} from 'react-redux';
import _ from 'lodash';
import {
    dataIsValidAfterDelete,
    dataIsValidAfterSave,
    dataValidityKey, documentListOrderStorageKey, email,
    listSelectionStorageKey,
    realtimeDataReducerKey,
    resourceDocuments,
    resourceRequests,
    userId,
    fullName,
    querySearch
} from '../constants';
import {RouteChildrenProps, withRouter} from 'react-router-dom';
import {parse, stringify} from "query-string";
import {fetchListData} from "../functions/fetchListData";
import {DataProvider} from "../fetchApi/tsTypes";
import {mapDispatchToProps} from "../fetchApi/dataProvider";
import {withTranslation} from "react-i18next";
import {TFunction} from "i18next";

interface EnhancedListProps extends MaterialTableProps<any> {
    resource: string;
    localStorageKey: string;
    documentTypeId?: string;
    documentUserId?: number;
    include?: Array<string | object>;
    onDataChanged?: () => void;
    forwardedRef: ForwardedRef<any>;
}

interface ListWithRefProps extends MaterialTableProps<any> {
    resource: string;
    localStorageKey: string;
    documentTypeId?: string;
    documentUserId?: number;
    include?: Array<string | object>;
    onDataChanged?: () => void;
    ref: ForwardedRef<any>;
}

interface ListProps extends EnhancedListProps, RouteChildrenProps {
    dataIsValid: boolean;
    dataProvider: DataProvider;
    dispatch: Dispatch;
    t: TFunction;
}

interface ListState {
    key: number;
}

const DEFAULT_SEARCH = '';
const DEFAULT_PAGE = 0;
const DEFAULT_PAGE_SIZE = 25;
const DEFAULT_SORT = 'id';
const DEFAULT_ORDER = 'desc';

const QUERY_PAGE = 'page';
const QUERY_PER_PAGE = 'perPage';
const QUERY_SORT = 'sort';
const QUERY_ORDER = 'order';
const QUERY_FILTER = 'filter';

const changeColumnsFormat = (columns: Array<Column<EditCellColumnDef>>): Array<Record<string, any>> => {
    const columnsArray: Array<Record<string, any>> = [];
    columns.forEach((col: Column<EditCellColumnDef>) => {
        columnsArray.push({field: col.field, hidden: col.hidden || false});
    });

    return columnsArray;
};

const getVisibleColumnByIndex = (columns: Array<Column<EditCellColumnDef>>, idx: number): Record<string, any> => {
    let idxVisible = -1;
    for (let i = 0; i < columns.length; i++) {
        if (!columns[i].hidden && !columns[i].hiddenByColumnsButton) {
            idxVisible++;
        }
        if (idxVisible === idx) {
            return {column: columns[i], idx: i};
        }
    }

    return {column: null, idx: -1};
};

const getColumnIfPresent = (columns: Array<Record<string, any>>, field: string | undefined): Record<string, any> => {
    for (let i = 0; i < columns.length; i++) {
        if (columns[i].field && columns[i].field === field) {
            return {column: columns[i], idx: i};
        }
    }

    return {column: null, idx: -1};
};

interface AnyRecord extends Record<string, any> {
    id: number;
}

class List extends React.Component<ListProps, ListState> {
    private readonly tableRef: React.RefObject<any>;
    private readonly columnsClone: Array<Column<EditCellColumnDef>>;
    private columns: Array<Column<EditCellColumnDef>>;
    private data: Array<object>;

    constructor(props: ListProps) {
        super(props);
        this.tableRef = React.createRef();
        this.columnsClone = _.cloneDeep(props.columns);
        this.columns = this.filterAndReorderColumns(props.columns, props);
        this.data = [];

        this.state = {
            key: 0
        };
    }

    saveColumnsToLocalStorage = (columnsArray: Array<Record<string, any>>): void => {
        const {localStorageKey} = this.props;

        let parsedListSelection = {};
        try {
            const storageSelection = localStorage.getItem(listSelectionStorageKey);
            parsedListSelection = storageSelection && JSON.parse(storageSelection);
        } catch (e) {
        }

        const selectionToSave = {...parsedListSelection, [localStorageKey]: columnsArray};
        localStorage.setItem(listSelectionStorageKey, JSON.stringify(selectionToSave));
    }

    readColumnsFromLocalStorage = (): Array<Record<string, any>> => {
        const {localStorageKey} = this.props;

        let parsedListSelection = {};
        try {
            const storageSelection = localStorage.getItem(listSelectionStorageKey);
            parsedListSelection = storageSelection && JSON.parse(storageSelection);
        } catch (e) {
        }

        return _.get(parsedListSelection, localStorageKey, null);
    }

    filterAndReorderColumns = (columns: Array<Column<EditCellColumnDef>>, localProps: ListProps): Array<Column<EditCellColumnDef>> => {
        // const columnsClone = [...columns];
        const columnsClone = _.cloneDeep(columns);

        const columnsFromStorage = this.readColumnsFromLocalStorage();

        let newColumns: Array<Column<EditCellColumnDef>> = [];
        if (columnsFromStorage) {
            for (let i = 0; i < columnsFromStorage.length; i++) {
                if (!columnsFromStorage[i].hasOwnProperty('field') || !columnsFromStorage[i].hasOwnProperty('hidden')) {
                    // wrong cached columns
                    console.log('wrong cached columns:', columnsFromStorage[i], columnsFromStorage);
                    return columns;
                }

                const columnObject = getColumnIfPresent(columns, columnsFromStorage[i].field);
                if (columnObject.column) {
                    columnObject.column.hidden = columnsFromStorage[i].hidden || false;
                    columnObject.column.hiddenByColumnsButton = columnsFromStorage[i].hidden || false;
                    delete(columnObject.column.tableData); // hack for not caching filters
                    newColumns.push(columnObject.column);
                }

                const columnObjectClone = getColumnIfPresent(columnsClone, columnsFromStorage[i].field);
                if (columnObjectClone.idx >= 0) {
                    columnsClone.splice(columnObjectClone.idx, 1);
                }
            }

            if (columnsClone.length > 0) {
                // new columns present
                // console.log('new columns added:', columnsClone);
                for (let i = 0; i < columnsClone.length; i++) {
                    const columnObject = getColumnIfPresent(columns, columnsClone[i].field as string);
                    delete(columnObject.column.tableData); // hack for not caching filters
                    newColumns.push(columnObject.column);
                }
            }
        } else {
            newColumns = [...columns];
        }

        // make sort as in query params
        let sort = parse(localProps.location.search)[QUERY_SORT];
        if (Array.isArray(sort)) {
            sort = sort[0];
        }
        let order = parse(localProps.location.search)[QUERY_ORDER];
        if (Array.isArray(order)) {
            order = order[0];
        }
        newColumns = this.updateDefaultSort(newColumns, sort, order);

        // make filters as in query params
        const filters = parse(localProps.location.search)[QUERY_FILTER];
        try {
            let jsonFilters;
            if (Array.isArray(filters)) {
                jsonFilters = JSON.parse(filters[0]);
            } else if (filters) {
                jsonFilters = JSON.parse(filters);
            }

            newColumns = this.updateDefaultFilters(newColumns, jsonFilters);
        } catch (e) {
            // console.log(e);
        }

        return newColumns;
    }

    handleSelectedColumnsChange = (column: Column<EditCellColumnDef>): void => {
        const idx = this.columns.findIndex((col: Column<EditCellColumnDef>) => col.field === column.field);

        if (column.hidden) {
            this.columns[idx].hidden = true;
            this.columns[idx].hiddenByColumnsButton = true;
        } else {
            this.columns[idx].hidden = false;
            this.columns[idx].hiddenByColumnsButton = false;
        }

        this.saveColumnsToLocalStorage(changeColumnsFormat(this.columns));
    }

    handleColumnDrag = (sourceIndex: number, destinationIndex: number): void => {
        //BUG: sometimes returned indexes not correct (+/- 1)
        const sourceColumnObj = getVisibleColumnByIndex(this.columns, sourceIndex);
        const destinationColumnIndex = getVisibleColumnByIndex(this.columns, destinationIndex).idx;

        this.columns.splice(sourceColumnObj.idx, 1);
        this.columns.splice(destinationColumnIndex, 0, sourceColumnObj.column);

        this.saveColumnsToLocalStorage(changeColumnsFormat(this.columns));
        // this.setState({key: Date.now()});
    }

    componentDidUpdate(prevProps: Readonly<ListProps>): void {
        const {dataIsValid: prevDataIsValid} = prevProps;
        const {dataIsValid} = this.props;

        if (prevDataIsValid && !dataIsValid) {
            this.onQueryChange();
        }
    }

    shouldComponentUpdate(nextProps: ListProps, nextState: ListState): boolean {
        const nextLocation = parse(nextProps.location.search);
        const currentLocation = parse(this.props.location.search);

        if (!_.isEmpty(currentLocation) && _.isEmpty(nextLocation)) { // case when menu clicked
            // console.log('nextLocation', nextLocation, 'currentLocation', currentLocation);
            this.dropDefaultFilters(nextProps);
            nextState.key = Date.now();
            return true;
        }

        if (this.props.hasOwnProperty('dataIsValid') && this.props.dataIsValid
            && nextProps.hasOwnProperty('dataIsValid') && !nextProps.dataIsValid) {
            this.onQueryChange();
        }

        return nextState.key !== this.state.key;
    }

    getDefaultSort = (): {sort: string, order: string} => {
        for (let i = 0; i < this.columns.length; i++) {
            if (this.columns[i].defaultSort) {
                return {sort: this.columns[i].field || DEFAULT_SORT,
                    order: this.columns[i].defaultSort?.toLowerCase() || DEFAULT_ORDER};
            }
        }

        return {sort: DEFAULT_SORT, order: DEFAULT_ORDER};
    }

    updateDefaultSort = (columns: Array<Column<EditCellColumnDef>>, sort: string | null, order: string | null): Array<Column<EditCellColumnDef>> => {
        if (sort && order) {
            for (let i = 0; i < columns.length; i++) {
                if (columns[i].field === sort) {
                    let orderTyped: 'asc' | 'desc' | undefined = undefined;
                    if (order === 'asc') {
                        orderTyped = 'asc';
                    } else if (order === 'desc') {
                        orderTyped = 'desc';
                    }
                    columns[i].defaultSort = orderTyped;
                } else {
                    if (columns[i].defaultSort) {
                        delete (columns[i].defaultSort);
                    }
                }
            }
        }

        return columns;
    }

    updateDefaultFilters = (columns: Array<Column<EditCellColumnDef>>, filters: Record<string, any>): Array<Column<EditCellColumnDef>> => {
        if (filters) {
            const filterKeys = Object.keys(filters);
            for (let i = 0; i < columns.length; i++) {
                const field = columns[i].field || '';
                if (field && filterKeys.includes(field)) {
                    columns[i]['defaultFilter'] = filters[field];
                }
            }
        }

        return columns;
    }

    dropDefaultFilters = (nextProps: ListProps): void => {
        this.columns = this.filterAndReorderColumns(this.columnsClone, nextProps);
    }

    updateHistory = (field: string, newValue: string, filters?: Filter<object>[]): void => {
        const {history, location} = this.props;
        const queryParams = parse(history.location.search);
        const queryParamsOriginal = _.cloneDeep(queryParams);
        let hasNewFilters = false;
        if ((filters && !_.isEmpty(filters))) {
            hasNewFilters = true;
            const filtersQuery = JSON.parse('{}');
            for (let i = 0; i < filters.length; i++) {
                if (filters[i].column && filters[i].column.field && filters[i].value) {
                    filtersQuery[filters[i].column.field || ''] = filters[i].value; // currently supports only operator '='
                }
            }

            queryParams[QUERY_FILTER] = JSON.stringify(filtersQuery);
        } else if (filters && _.isEmpty(filters) && queryParams[QUERY_FILTER]) {
            delete queryParams[QUERY_FILTER];
            history.push({
                ...location,
                search: stringify(queryParams),
            });
        }

        if (_.isEmpty(queryParams) && field === querySearch && !newValue && !hasNewFilters) {
            return; // avoid case when search makes query with defaults
        }

        if (queryParams[field] || (!queryParams[field] && newValue)) {
            if (newValue) {
                queryParams[field] = newValue;
            } else {
                delete queryParams[field];
            }
        }

        // if (!queryParams[querySearch]) {
        //     queryParams[querySearch] = String(DEFAULT_SEARCH);
        // }

        if (queryParams[QUERY_PAGE] || queryParams[QUERY_PER_PAGE] || queryParams[QUERY_SORT] || queryParams[QUERY_ORDER]) {
            const defaultSort = this.getDefaultSort();

            if (!queryParams[QUERY_PAGE]) {
                queryParams[QUERY_PAGE] = String(DEFAULT_PAGE);
            }
            if (!queryParams[QUERY_PER_PAGE]) {
                queryParams[QUERY_PER_PAGE] = String(DEFAULT_PAGE_SIZE);
            }
            if (!queryParams[QUERY_SORT]) {
                queryParams[QUERY_SORT] = defaultSort.sort;
            }
            if (!queryParams[QUERY_ORDER]) {
                queryParams[QUERY_ORDER] = defaultSort.order;
            }
        }

        if (!_.isEqual(queryParamsOriginal, queryParams)) {
            // console.log('NEW queryParams', queryParams, field, newValue, filters);
            history.push({
                ...location,
                search: stringify(queryParams),
            });
        }
    }

    onChangePage = (page: number): void => {
        this.updateHistory(QUERY_PAGE, String(page));
    }

    onChangeRowsPerPage = (perPage: number): void => {
        this.updateHistory(QUERY_PER_PAGE, String(perPage));
    }

    onOrderChange = (orderBy: number, orderDirection: string): void => {
        if (orderBy >= 0 && this.columns[orderBy]) {
            this.updateHistory(QUERY_SORT, this.columns[orderBy].field || DEFAULT_SORT);
            this.updateHistory(QUERY_ORDER, orderDirection?.toLowerCase() || DEFAULT_ORDER);
        }
    }

    loadData = (query: Query<object>): Promise<QueryResult<AnyRecord>> => {
        return new Promise(resolve => {
            const {filters, orderBy, orderDirection, page, pageSize: rowsPerPage, search} = query;
            const {dataProvider, dispatch, resource, documentUserId, documentTypeId, include, onDataChanged} = this.props;

            this.updateHistory(querySearch, search, filters);

            const order = orderBy?.field && orderDirection ?
                [`${orderBy.field} ${orderDirection.toUpperCase()}`] :
                undefined;

            const whereFilter: Record<string, any> = {
                userId: documentUserId ? documentUserId : undefined, // HACK for user-document-list
                documentTypeId: documentTypeId ? documentTypeId : undefined, // HACK for doc list
                searchSourceNameKey: search || undefined,
            };

            // HACK for supplier
            const documentCountFilter = filters.find(({column: col}) => col.field === 'documentsCount');
            if (documentCountFilter) {
                whereFilter.onlyHotSuppliers = true;
            }

            filters && filters.forEach(({column, value}) => {
                if (column.field === email && resource === resourceRequests) { // HACK for request
                    whereFilter[userId] = value;
                } else if (column.field === fullName && (resource === resourceRequests || resource === resourceDocuments)) {  // HACK
                    whereFilter[userId] = value;
                } else if (column.field) {
                    whereFilter[column.field] = value;
                }
            });

            const fields = JSON.parse('{}');
            for (let i = 0; i < this.columns.length; i++) {
                let localField = this.columns[i].field || '';
                localField = localField.split('.')[0];
                if (localField) {
                    fields[localField] = true;
                }
            }

            if (resource === resourceDocuments) { // HACK for recognitionStatus Tooltip
                fields['lastRecognitionError'] = true;
            }

            fetchListData(dataProvider, dispatch, {
                order,
                limit: rowsPerPage,
                filter: whereFilter,
                include,
                skip: page * rowsPerPage,
                page,
                fields: fields
            }, resource)
                .then(
                    (result) => {
                        const {data} = result;
                        this.data = data;
                        if (onDataChanged) {
                            onDataChanged();
                        }

                        if (resource === resourceDocuments) { // HACK for documents
                            localStorage.setItem(documentListOrderStorageKey, JSON.stringify({
                                filters: whereFilter,
                                sorts: order,
                                skip: page * rowsPerPage
                            }));
                        }
                        resolve(result);
                    }
                )
                .catch(resolve);
        });
    };

    onQueryChange = (): void => {
        this.tableRef?.current?.onQueryChange();
    }

    getData = (): Array<object> => {
        return this.data;
    }

    render(): React.ReactElement | null {
        const {data, options, columns, actions, t: translate, ...rest} = this.props;
        const {key} = this.state;

        const actionsAsList = actions || [];

        if (data) { // removing warning
        }

        // console.log('re-render!!');
        if (!this.columns.length || !columns.length) {
            // If MaterialTable receives empty columns array on first render it will not handle default sort if further renders
            return null;
        }

        const initialPage = Number(parse(this.props.location.search)[QUERY_PAGE]) || DEFAULT_PAGE;
        const searchText = parse(this.props.location.search)[querySearch] || DEFAULT_SEARCH;
        const defaultListOptions: MaterialTableProps<any>['options'] = {
            columnsButton: true,
            searchFieldAlignment: 'left',
            tableLayout: 'auto',
            draggable: true,
            pageSizeOptions: [25, 50, 100],
            pageSize: Number(parse(this.props.location.search)[QUERY_PER_PAGE] || DEFAULT_PAGE_SIZE),
            emptyRowsWhenPaging: false,
            padding: 'dense',
            loadingType: 'linear',
        };

        return (
            <React.Fragment>
                <ListWrappingDiv>
                    <MaterialTable
                        icons={ListIcons}
                        columns={this.columns}
                        data={this.loadData}
                        options={{...defaultListOptions, ...options, initialPage, searchText}}
                        tableRef={this.tableRef}
                        // onSearchChange={this.onSearchChange} NOT WORKING for Promise in getData()
                        // onFilterChanged={this.onFilterChanged} NOT WORKING for Promise in getData()
                        onChangePage={this.onChangePage}
                        onChangeRowsPerPage={this.onChangeRowsPerPage}
                        onOrderChange={this.onOrderChange}
                        onChangeColumnHidden={this.handleSelectedColumnsChange}
                        onColumnDragged={this.handleColumnDrag}
                        key={key}
                        actions={[
                            ...actionsAsList,
                            {
                                icon: 'refresh',
                                tooltip: translate('common.actions.update_data'),
                                onClick: (): void => this.onQueryChange(),
                                isFreeAction: true,
                            },
                        ]}
                        {...rest}
                    />
                </ListWrappingDiv>
            </React.Fragment>
        );
    }
}

const mapStateToProps = (state: object, props: EnhancedListProps): object => {
    const {resource} = props;
    const dataValidity = _.get(state, [realtimeDataReducerKey, resource, dataValidityKey], null);

    let dataIsValid = true;
    if (dataValidity && (resource !== resourceDocuments
        || resource === resourceDocuments && dataValidity.documentType === props.documentTypeId)) { // update only required doc type
        dataIsValid = dataValidity[dataIsValidAfterSave] && dataValidity[dataIsValidAfterDelete];
    }

    return {
        dataIsValid,
    };
};

const EnhancedList = compose<React.ComponentType<EnhancedListProps>>(
    withRouter,
    withTranslation(),
    connect(mapStateToProps, mapDispatchToProps),
)(List);

const ListWithRef = React.forwardRef((props: ListWithRefProps, ref) => (
    <EnhancedList {...props} forwardedRef={ref}/>
));

export default ListWithRef;
